<template>
  <div>
    <transition name="fade">
      <LoadingView
        v-if="clubLoading"
        :loading="loading"
      />
    </transition>

    <transition name="fade">
      <div
        v-if="!clubLoading"
        class="w-full h-screen min-h-screen text-gray-700"
      >
        <div
          class="bg-center bg-cover w-full overflow-hidden"
          style="
            background-image: url('https://tools.playmore.golf/Compare/img/bg2.jpg');
            height: 400px;
          "
        />

        <div class="">
          <div class="max-w-5xl ml-auto mr-auto -m-36 pl-2 pr-2 md:p-0">
            <div
              v-if="club !== null && !club.Authorized"
              class="flex justify-between items-center bg-white overflow-hidden mb-2 text-gray-800 border text-sm p-1"
            >
              <div class="p-3 pl-4 pr-4 flex items-center leading-none">
                <i
                  class="fas fa-info-circle text-gray-800 mr-2"
                  style="margin-top: 1px"
                />
                <span class="mr-2 font-semibold">Not logged in</span>
                You must be logged in to be able to access live data.
              </div>

              <div class="mr-1">
                <a
                  class=""
                  href="https://members.playmore.golf"
                  target="_blank"
                >
                  <div
                    class="bg-secondary-100 text-white hover:bg-secondary-200 leading-none p-2 pl-2 pr-2 transition-all duration-500"
                  >
                    Log in
                  </div>
                </a>
              </div>
            </div>

            <div class="shadow">
              <div class="bg-gradient-to-b from-gray-700 to to-gray-900 t-lg px-9 py-8">
                <div class="flex justify-between items-center mb-4">
                  <div
                    class="font-bold text-white heading leading-none"
                    :class="club !== null && club.ClubName.length > 22 ? 'text-3xl' : 'text-4xl'"
                  >
                    <span v-if="club === null">Compare your flexible membership</span>
                    <span v-else>{{ club.ClubName }} Membership Simulator</span>
                  </div>

                  <div>
                    <div class="flex">
                      <div class="w-4 h-4" />
                      <div class="w-4 h-4 bg-pmg-400" />
                      <div class="w-4 h-4" />
                    </div>
                    <div class="flex">
                      <div class="w-4 h-4 bg-pmg-400" />
                      <div class="w-4 h-4 bg-pmg-800" />
                      <div class="w-4 h-4 bg-pmg-100" />
                    </div>
                    <div class="flex">
                      <div class="w-4 h-4" />
                      <div class="w-4 h-4 bg-pmg-100" />
                      <div class="w-4 h-4" />
                    </div>
                  </div>
                </div>

                <div class="text-sm text-gray-100 mt-8 leading-relaxed">
                  <div>
                    Explore the possible outcomes of your own flexible membership platform using our
                    simulator and compare the results to what you could achieve with
                    <a
                      href="https://playmore.golf"
                      target="_blank"
                      class="hover:text-white cursor-pointer transition-all duration-500 font-semibold"
                    >
                      PlayMoreGolf
                    </a>
                    over the next 12 months.
                  </div>
                </div>
              </div>

              <div
                class="flex justify-between items-center z-50 p-2 pr-8 pl-8 text-gray-500 bg-white text-lg shadow border-l border-r border-b"
              >
                <div>
                  <div
                    title="Reset form"
                    class="cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.97rem; margin-top: 2px; margin-bottom: -2px"
                    @click="reset()"
                  >
                    <i class="fas fa-undo leading-none" />
                  </div>
                </div>

                <div class="flex items-center">
                  <div
                    title="Copy link to clipboard"
                    class="mr-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    style="font-size: 0.95rem; margin-bottom: 0px; padding-right: 5px"
                    @click="copyLink()"
                  >
                    <i class="fas fa-link leading-none" />
                  </div>
                  <a
                    :href="
                      'mailto:?subject=PlayMoreGolf membership platform simulator&body=' + getLink()
                    "
                  >
                    <div
                      title="Send via email"
                      class="ml-1 cursor-pointer hover:text-gray-700 transition-all duration-500"
                    >
                      <i class="fas fa-envelope leading-none" />
                    </div>
                  </a>
                </div>
              </div>

              <div class="p-8 pr-7 border border-t-0 bg-white b-lg">
                <div class="flex">
                  <div class="flex w-2/3 pr-6">
                    <div class="w-1/2 pr-6">
                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="flexiJoinersValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ flexiJoinersValid }}
                          </div>
                          Current flexi members
                          <div
                            class="inline-block tool"
                            data-tooltip="The current number of flexible members at your club."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="pt-1 pb-1 border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <input
                            v-model="currentFlexiMembers"
                            type="number"
                            class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                          />
                        </div>
                      </div>

                      <div class="mb-10">
                        <div class="text-sm font-medium text-gray-500 focus-within:text-gray-800">
                          Current flexible membership price
                          <!-- <div
                            class="inline-block tool"
                            data-tooltip="The proportional cost of your flexible membership in comparison to your traditional membership."
                          >
                            <i class="fad fa-info-circle duration-250 transition-all cursor-pointer" />
                          </div> -->
                        </div>
                        <div @click="cohortDropdownOpen = !cohortDropdownOpen">
                          <div
                            class="flex justify-between items-center pt-1 pb-1 text-gray-600 hover:text-gray-800 select-none cursor-pointer text-lg border-b focus-within:border-b-2 transition-all duration-500"
                          >
                            <div>£{{ flexiblePackageCost }}</div>
                            <div class="mr-2">
                              <i class="fas fa-chevron-down text-base" />
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="cohortDropdownOpen"
                          class="relative w-full"
                        >
                          <div
                            class="absolute mt-2 z-40 border shadow-lg bg-white w-full overflow-hidden"
                          >
                            <div
                              v-for="cohort in visibleCohorts"
                              :key="cohort.index"
                              class="flex justify-between items-center p-2 pl-3 pr-3 bg-white hover:bg-gray-100 transition-all duration-200 cursor-pointer"
                              @click="
                                flexiblePackageCost = cohorts[cohort.index];
                                cohortDropdownOpen = false;
                              "
                            >
                              <div>£{{ cohort.value }}</div>
                              <div
                                v-if="flexiblePackageCost === cohort.value"
                                class="mr-2"
                              >
                                <i class="far fa-check" />
                              </div>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="cohortDropdownOpen"
                          class="fixed top-0 left-0 z-30 w-full min-w-full h-full min-h-full"
                          @click="cohortDropdownOpen = false"
                        />
                      </div>

                      <div
                        v-if="showAllFields"
                        class="text-gray-500 focus-within:text-gray-800"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          Employee hourly cost
                          <div
                            class="inline-block tool"
                            data-tooltip="The hourly cost of an employee including national insurance and pension contributions."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£{{ hourlyRate }}</div>
                        </div>
                      </div>
                    </div>

                    <div class="w-1/2">
                      <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          Current PlayMoreGolf Partnership Fee
                          <div
                            class="inline-block tool"
                            data-tooltip="The price of your partnership fee with PlayMoreGolf."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="pmgPartnershipFee"
                            type="number"
                            class="outline-none w-full"
                          />
                        </div>
                      </div>

                      <div class="text-gray-500 focus-within:text-gray-800">
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="socialSpendValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ socialSpendValid }}
                          </div>
                          Monthly digital marketing spend
                          <div
                            class="inline-block tool"
                            data-tooltip="Your total monthly budget on social media including paid adverts/boosted posts and agency costs."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="ownFlexi.marketingSpendMonth"
                            type="number"
                            class="outline-none w-full"
                          />
                        </div>
                      </div>

                      <div
                        v-if="false"
                        class="mt-10 text-gray-500 focus-within:text-gray-800"
                      >
                        <div class="text-sm font-medium transition-all duration-500">
                          <div
                            v-if="socialSpendValid !== true"
                            class="-mt-5 text-sm text-red-600"
                          >
                            {{ socialSpendValid }}
                          </div>
                          Monthly Digital Marketing Spend
                          <div
                            class="inline-block tool"
                            data-tooltip="Your total monthly budget on social media including paid adverts/boosted posts and agency costs."
                          >
                            <i
                              class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                            />
                          </div>
                        </div>
                        <div
                          class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                        >
                          <div>£</div>
                          <input
                            v-model="ownFlexi.marketingSpendMonth"
                            type="number"
                            class="outline-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="w-1/3 pl-0">
                    <div class="mb-10 text-gray-500 focus-within:text-gray-800">
                      <div class="text-sm font-medium transition-all duration-500">
                        Additional license fee costs
                        <div
                          class="inline-block tool"
                          data-tooltip="The total sum of any annual licensing fees you would have to pay for your flexible module/add-on through your tee sheet provider."
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.licenceFee"
                          type="number"
                          class="outline-none w-full"
                        />
                      </div>
                    </div>

                    <div class="my-10 text-gray-500 focus-within:text-gray-800">
                      <div class="text-sm font-medium transition-all duration-500">
                        <div
                          v-if="marketingSpendValid !== true"
                          class="-mt-5 text-sm text-red-600"
                        >
                          {{ marketingSpendValid }}
                        </div>
                        Monthly other marketing costs
                        <div
                          class="inline-block tool"
                          data-tooltip="Your total monthly budget for all other marketing activities including printed adverts, design work etc."
                        >
                          <i
                            class="fad fa-info-circle duration-250 transition-all cursor-pointer"
                          />
                        </div>
                      </div>
                      <div
                        class="flex pt-1 pb-1 text-gray-600 focus-within:text-gray-900 text-lg border-b focus-within:border-b-2 transition-all duration-500 focus-within:border-secondary-100"
                      >
                        <div>£</div>
                        <input
                          v-model="ownFlexi.marketingSpendOtherMonth"
                          type="number"
                          class="outline-none text-gray-700 group-focus:text-gray-900 text-lg w-full"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div :class="showAllFields ? 'mt-10' : ''">
                  <div
                    class="w-full min-w-full text-sm p-4 pl-4 pr-4 text-center font-medium transition-all duration-500 text-white select-none cursor-pointer uppercase"
                    :class="
                      validForm
                        ? 'bg-secondary-100 hover:bg-secondary-200'
                        : 'bg-gray-300 text-gray-700 cursor-not-allowed'
                    "
                    @click="simulate()"
                  >
                    Run our membership platform simulator
                    <span v-if="club !== null">for {{ club.ClubName }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="p-2 text-2xl text-center mt-40 mb-6 transition-all duration-500 linear-wipe font-bold"
            :class="loading ? 'opacity-100' : 'opacity-0'"
          >
            Running 20,000 simulations
          </div>

          <div>
            <Chart
              :comparison-results="comparisonResults"
              :years="years"
              :selected-tab="selectedTab"
            />
          </div>

          <div
            v-if="comparisonResults !== null && comparisonResults !== undefined"
            class="max-w-4xl ml-auto mr-auto mt-12 pb-12"
          >
            <Table
              :comparison-results="comparisonResults"
              :years="years"
              :link="getLink()"
              :selected-tab-prop="selectedTab"
              @selectTab="selectTab"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import VueSlider from 'vue-slider-component';
  import { compareFlexibleProducts } from '@/app/shared/services/calculatorService/calculatorService';
  import { submitForm } from '@/app/shared/services/hubspotService/hubspotService';
  import { GetClub } from '@/app/shared/services/clubService/clubService';
  import LoadingView from '@/app/shared/components/base/loading/loadingView.vue';
  import Chart from '@/app/shared/components/partner/chart/chart.vue';
  import Table from '@/app/shared/components/partner/table/table.vue';
  import 'vue-slider-component/theme/antd.css';

  export default {
    name: 'Calculator',

    components: {
      Chart,
      Table,
      VueSlider,
      LoadingView,
    },

    data() {
      return {
        currency: '£',
        years: 1,
        formatter1: '{value}%',
        selectedTab: 'Summary',
        showAllFields: false,
        shareMenuOpen: false,
        cohorts: [320, 400, 448, 480, 560, 720, 800],
        visibleCohorts: [
          { value: 320, index: 0 },
          { value: 400, index: 1 },
          { value: 448, index: 2 },
          { value: 480, index: 3 },
          { value: 560, index: 4 },
          { value: 720, index: 5 },
          { value: 800, index: 6 },
        ],
        cohortDropdownOpen: false,

        pmgPartnershipFee: 2400,
        flexiblePackageCost: 375,
        hourlyRate: 17.92,
        currentFlexiMembers: 40,

        tooltip: { isVisible: true, format: 'C2' },
        ownFlexi: {
          pmgProduct: false,
          licenceFee: 1800,
          emailHours: 2.5,
          retentionModifier: 0.1,
          earningsReceived: 1,
          marketingSpendMonth: 350,
          marketingSpendOtherMonth: 150,
          cardFee: 0.03,
        },

        pmgFlexi: {
          pmgProduct: true,
          licenceFee: 2400,
          emailHours: 0,
          retentionModifier: 0,
          earningsReceived: 0.62,
          marketingSpendMonth: 0,
          marketingSpendOtherMonth: 0,
          cardFee: 0,
        },

        comparisonResults: null,
        loading: false,
        linkCopied: false,
        club: null,
      };
    },

    computed: {
      clubLoading() {
        if (
          this.$route.params.clubId !== null
          && this.$route.params.clubId > 0
          && this.club === null
        ) {
          return true;
        }

        return false;
      },

      flexiJoinersValid() {
        if (this.currentFlexiMembers >= 200) {
          return 'Value too high';
        }

        return true;
      },

      marketingSpendValid() {
        if (this.ownFlexi.marketingSpendMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      socialSpendValid() {
        if (this.ownFlexi.marketingSpendOtherMonth <= 0) {
          return 'Must be greater than 0';
        }
        return true;
      },

      validForm() {
        if (
          this.flexiJoinersValid == true
          && this.marketingSpendValid === true
          && this.socialSpendValid === true
        ) {
          return true;
        }
        return false;
      },
    },

    watch: {
      flexiblePackageCost() {
        return 0.62;
      },

      linkCopied() {
        setTimeout(() => {
          this.linkCopied = false;
        }, 7000);
      },
    },

    mounted() {
      this.getClub();
      this.assignParams();
    },

    methods: {
      getClub() {
        const { clubId } = this.$route.params;

        const { query } = this.$route;

        if (clubId !== null && clubId > 0) {
          GetClub(clubId).then((data) => {
            this.club = data;

            if (this.$route.query.useLiveData) {
              if (data.Authorized) {
                this.currentFlexiMembers = data.CurrentFlexiMembers;
              }
              this.flexiblePackageCost = this.cohorts[data.Cohort];
            }
          });
        }
      },

      simulate() {
        if (!this.loading) {
          this.loading = true;
          this.comparisonResults = null;

          compareFlexibleProducts(this.getOwnFlexi(), this.getPMGFlexi(), 1, true).then((data) => {
            setTimeout(() => (this.loading = false), 2000);
            data.Product1 = data.Product1[0];
            data.Product2 = data.Product2[0];
            setTimeout(() => (this.comparisonResults = data), 2000);
          });
        }
      },

      getPMGFlexi() {
        const pmgFlexi = { ...this.pmgFlexi };
        pmgFlexi.flexiblePackageCost = this.flexiblePackageCost;
        pmgFlexi.pmgPartnershipFee = this.pmgPartnershipFee;
        pmgFlexi.hourlyRate = this.hourlyRate;
        pmgFlexi.SocialSpendMonth = this.pmgFlexi.marketingSpendOtherMonth;
        pmgFlexi.currentFlexiMembers = this.currentFlexiMembers;
        return pmgFlexi;
      },

      getOwnFlexi() {
        const ownFlexi = { ...this.ownFlexi };
        ownFlexi.flexiblePackageCost = this.flexiblePackageCost;
        ownFlexi.pmgPartnershipFee = this.pmgPartnershipFee;
        ownFlexi.hourlyRate = this.hourlyRate;
        ownFlexi.SocialSpendMonth = this.ownFlexi.marketingSpendOtherMonth;
        ownFlexi.currentFlexiMembers = this.currentFlexiMembers;
        return ownFlexi;
      },

      assignParams() {
        const { query } = this.$route;
        if (query.showAllFields !== undefined) {
          this.showAllFields = query.showAllFields === 'true';
        }

        if (query.licenseFee !== undefined) {
          this.ownFlexi.licenceFee = parseInt(query.licenseFee);
        }

        if (query.cohort !== undefined) {
          this.flexiblePackageCost = query.cohort;
        }

        if (query.pmgFee !== undefined) {
          this.pmgPartnershipFee = parseInt(query.pmgFee);
        }

        if (query.hourlyRate !== undefined) {
          this.hourlyRate = parseFloat(query.hourlyRate);
        }

        if (query.currentFlexi !== undefined) {
          this.currentFlexiMembers = parseInt(query.currentFlexi);
        }

        if (query.monthlyMarketing !== undefined) {
          this.ownFlexi.marketingSpendMonth = parseInt(query.monthlyMarketing);
        }

        if (query.monthlyMarketingOther !== undefined) {
          this.ownFlexi.marketingSpendOtherMonth = parseInt(query.monthlyMarketingOther);
        }

        // const flexiProportion = urlParams.get('flexiProportion');
        // if (flexiProportion !== null) {
        //   this.flexiblePackageCostProportion = parseInt(flexiProportion);
        // }
      },

      reset() {
        const r = confirm('Are you sure you want to reset the form to the default values?');
        if (r === true) {
          this.pmgPartnershipFee = 2400;
          this.flexiblePackageCost = 375;
          this.hourlyRate = 17.92;
          this.currentFlexiMembers = this.club.CurrentFlexiMembers;
          this.ownFlexi.licenceFee = 1800;
          this.ownFlexi.marketingSpendMonth = 350;
          this.ownFlexi.marketingSpendOtherMonth = 150;
        }
      },

      copyLink() {
        const textArea = document.createElement('textarea');
        textArea.style.position = 'fixed';
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = 0;
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        textArea.value = `${process.env.VUE_APP_SITE_URL}#/Partner/${this.$route.params.clubId}?currentFlexi=${this.currentFlexiMembers}&licenseFee=${this.ownFlexi.licenceFee}&pmgFee=${this.pmgPartnershipFee}&cohort=${this.flexiblePackageCost}&monthlyMarketing=${this.ownFlexi.marketingSpendMonth}&monthlyMarketingOther=${this.ownFlexi.marketingSpendOtherMonth}`;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          // const msg = successful ? 'successful' : 'unsuccessful';
          this.linkCopied = true;
        } catch (err) {
          console.log('Oops, unable to copy');
        }
        document.body.removeChild(textArea);
      },

      getLink() {
        // const link = `${process.env.VUE_APP_SITE_URL}?licenseFee%3D${this.ownFlexi.licenceFee}%26fullMember%3D${this.fullMemberCost}%26currentFlexi%3D${this.currentFlexiMembers}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendMonth}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendOtherMonth}`;
        // const link = `${process.env.VUE_APP_SITE_URL}?licenseFee=${this.ownFlexi.licenceFee}&fullMember=${this.fullMemberCost}&hourlyRate=${this.hourlyRate}&currentFlexi=${this.currentFlexiMembers}&monthlyMarketing=${this.ownFlexi.marketingSpendMonth}&monthlySocial=${this.ownFlexi.socialSpendMonth}`;
        const link = `${process.env.VUE_APP_SITE_URL}#/Partner/${this.$route.params.clubId}?currentFlexi%3D${this.currentFlexiMembers}%26licenseFee%3D${this.ownFlexi.licenceFee}%26pmgFee%3D${this.pmgPartnershipFee}%26cohort%3D${this.flexiblePackageCost}%26monthlyMarketing%3D${this.ownFlexi.marketingSpendMonth}%26monthlyMarketingOther%3D${this.ownFlexi.marketingSpendOtherMonth}`;
        return link;
      },

      selectTab(tab) {
        this.selectedTab = tab;
      },
    },
  };
</script>

<style lang="scss">
  .linear-wipe {
    text-align: center;

    background: linear-gradient(to right, #bbd673 20%, #add24e 40%, #add24e 60%, #bbd673 80%);
    background-size: 200% auto;

    color: #000;
    background-clip: text;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;

    animation: shine 2s linear infinite;
    @keyframes shine {
      to {
        background-position: 200% center;
      }
    }
  }

  .vue-slider-process {
    background-color: #add24e;
  }

  .vue-slider:hover .vue-slider-process {
    transition: all;
    transition-duration: 500ms;
    background-color: #add24e;
  }

  .vue-slider-dot-handle {
    border: 2px solid #add24e;
  }

  .vue-slider:hover .vue-slider-dot-handle:hover {
    border-color: #add24e;
  }

  .vue-slider-dot-handle-focus {
    border-color: rgb(248, 168, 74);
    -webkit-box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
    box-shadow: 0 0 0 5px rgba(248, 168, 74, 0.2);
  }
</style>

<style src="../assets/site.css"></style>
